// Spec: client-side structured logger.
//
// Drop-in replacement for ad-hoc `console.*` calls. Every log entry carries
// a level, a session correlation ID, an ISO timestamp, the current page,
// and an optional context object. Entries land in:
//   1. `console` (in dev) for instant feedback
//   2. an in-memory ring buffer (last 500) for the /dev/logs page
//   3. a server-side pino logger via POST /api/clientlog (batched)
//
// The buffer flushes:
//   - on every `error` / `fatal` (immediate, single-shot)
//   - every 5s if anything in the buffer is unsent
//   - on `beforeunload` via `navigator.sendBeacon`
//
// The session correlation ID is generated once per page load and persisted
// in `sessionStorage` so a refresh keeps the same session — useful for
// reconciling pre-refresh logs on the server.

import { redactLogContext } from './redact';
import { browserDetails as parseBrowserDetails } from './browserDetails';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

export interface LogEntry {
    /** Opaque event identity used only to deduplicate transport retries. */
    eventId: string;
    /** ISO 8601 timestamp. */
    ts: string;
    level: LogLevel;
    msg: string;
    /** Correlation ID for the browser session. Stable across reloads. */
    sessionId: string;
    /** `location.pathname + location.search` when the line was emitted. */
    page: string;
    release: string;
    appVersion?: string;
    commit?: string | null;
    commitSha?: string | null;
    buildSource?: string;
    buildDirty?: boolean | null;
    playbackAttemptId?: string;
    browser: string;
    platform: string;
    /** Free-form structured context; serialised as JSON on the wire. */
    ctx?: Record<string, unknown>;
}

const LEVEL_RANK: Record<LogLevel, number> = {
    debug: 10,
    info: 20,
    warn: 30,
    error: 40,
    fatal: 50,
};

const BUFFER_LIMIT = 500;
const BATCH_INTERVAL_MS = 5_000;
const ERROR_FLUSH_THROTTLE_MS = 1_000;
const RATE_LIMIT_BACKOFF_MS = 15_000;
const ENDPOINT = '/api/clientlog';
const MAX_BATCH_ENTRIES = 50;
const MAX_BATCH_BYTES = 28 * 1024;
const MAX_RETRIES = 3;
const RETRY_DELAYS_MS = [1_000, 5_000, 15_000] as const;
const REQUEST_TIMEOUT_MS = 10_000;
const PREAUTH_BUFFER_LIMIT = 50;

const ringBuffer: LogEntry[] = [];
const pending: Array<{ entry: LogEntry; retries: number }> = [];
const preAuthPending: Array<{ event: { eventId: string; type: string; release: string; browser: string; clientTs: string }; retries: number }> = [];
const transportDrops = { overflow: 0, oversized: 0, rejected: 0, retriesExhausted: 0 };
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushInFlight = false;
let inFlightCount = 0;
let preAuthFlushInFlight = false;
let preAuthFlushTimer: ReturnType<typeof setTimeout> | null = null;
let retryFlushRequested = false;
let remoteAuthBlocked = false;
let remoteBackoffUntil = 0;
let lastImmediateErrorFlushAt = Number.NEGATIVE_INFINITY;
let activePlaybackAttemptId: string | null = null;

const SENSITIVE_PAGE_QUERY_KEYS = [
    't',
    'token',
    'jwt',
    'url',
    'password',
    'username',
    'user',
    'api_key',
    'apikey',
    'pin',
    'cursor',
    'event',
    'code',
    'usercode',
    'devicecode',
    'next',
] as const;
const SENSITIVE_PAGE_QUERY_KEY_SET = new Set<string>(SENSITIVE_PAGE_QUERY_KEYS);

function browserDetails(): { browser: string; platform: string } {
    const nav = typeof navigator === 'undefined'
        ? null
        : navigator as Navigator & { userAgentData?: { platform?: string } };
    return parseBrowserDetails(nav?.userAgent ?? '', nav?.userAgentData?.platform || nav?.platform || 'unknown');
}

/** Redact secrets and opaque discovery keys before a page enters any log buffer. */
export function redactPageForLog(raw: string): string {
    if (!raw) return raw;
    try {
        const page = new URL(raw, 'http://nodecast.local');
        for (const key of Array.from(page.searchParams.keys())) {
            const lower = key.toLowerCase();
            if (SENSITIVE_PAGE_QUERY_KEY_SET.has(lower)) {
                page.searchParams.set(key, '[REDACTED]');
            } else if (lower === 'redirect') {
                // Redirects are the one nested URL we keep for diagnostics;
                // redact its query once without recursively interpreting an
                // arbitrary URL-shaped value or changing redirect semantics.
                const nestedRaw = page.searchParams.get(key);
                if (!nestedRaw) continue;
                const nested = new URL(nestedRaw, page.origin);
                for (const nestedKey of Array.from(nested.searchParams.keys())) {
                    if (SENSITIVE_PAGE_QUERY_KEY_SET.has(nestedKey.toLowerCase())) {
                        nested.searchParams.set(nestedKey, '[REDACTED]');
                    }
                }
                page.searchParams.set(key, `${nested.pathname}${nested.search}${nested.hash}`);
            }
        }
        return `${page.pathname}${page.search}${page.hash}`;
    } catch {
        return '';
    }
}

function remoteTransportEnabled(): boolean {
    if (import.meta.env.MODE !== 'test') return true;
    return (globalThis as unknown as { __NODECAST_ENABLE_CLIENTLOG_TRANSPORT_FOR_TESTS__?: boolean })
        .__NODECAST_ENABLE_CLIENTLOG_TRANSPORT_FOR_TESTS__ === true;
}

function getSessionId(): string {
    const KEY = 'nodecast.log.sessionId';
    try {
        const existing = sessionStorage.getItem(KEY);
        if (existing) return existing;
        const fresh = crypto.randomUUID();
        sessionStorage.setItem(KEY, fresh);
        return fresh;
    } catch {
        // sessionStorage may be unavailable (Safari private mode, etc.)
        return crypto.randomUUID();
    }
}

const SESSION_ID = getSessionId();

function getLevelThreshold(): LogLevel {
    // Override via ?log=debug or localStorage('log.level','debug').
    try {
        const params = new URLSearchParams(location.search);
        const fromQuery = params.get('log');
        if (fromQuery && fromQuery in LEVEL_RANK) return fromQuery as LogLevel;
        const fromStorage = localStorage.getItem('log.level');
        if (fromStorage && fromStorage in LEVEL_RANK) return fromStorage as LogLevel;
    } catch {
        // localStorage may be unavailable; fall through.
    }
    return import.meta.env.DEV ? 'debug' : 'info';
}

let threshold = getLevelThreshold();

function shouldEmit(level: LogLevel): boolean {
    return LEVEL_RANK[level] >= LEVEL_RANK[threshold];
}

function validPlaybackAttemptId(id: unknown): id is string {
    return typeof id === 'string' && /^[A-Za-z0-9_-]{8,64}$/.test(id);
}

function pushEntry(level: LogLevel, msg: string, ctx?: Record<string, unknown>): LogEntry {
    const browser = typeof __NODECAST_BUILD__ === 'undefined'
        ? { browser: 'unknown', platform: 'unknown' }
        : browserDetails();
    const safeContext = redactLogContext(ctx);
    const explicitAttemptId = safeContext?.playbackAttemptId ?? safeContext?.playTraceId;
    const playbackAttemptId = validPlaybackAttemptId(explicitAttemptId)
        ? explicitAttemptId
        : activePlaybackAttemptId;
    const entry: LogEntry = {
        ts: new Date().toISOString(),
        eventId: crypto.randomUUID(),
        level,
        msg,
        sessionId: SESSION_ID,
        page: typeof location !== 'undefined'
            ? redactPageForLog(location.pathname + location.search)
            : '',
        release: typeof __NODECAST_BUILD__ === 'undefined'
            ? 'unknown'
            : `${__NODECAST_BUILD__.version}+${__NODECAST_BUILD__.commit ?? 'unknown'}${__NODECAST_BUILD__.dirty === true ? '.dirty' : ''}`.slice(0, 96),
        appVersion: typeof __NODECAST_BUILD__ === 'undefined' ? 'unknown' : __NODECAST_BUILD__.version,
        commit: typeof __NODECAST_BUILD__ === 'undefined' ? null : __NODECAST_BUILD__.commit,
        commitSha: typeof __NODECAST_BUILD__ === 'undefined' ? null : __NODECAST_BUILD__.commit,
        buildSource: typeof __NODECAST_BUILD__ === 'undefined' ? 'unknown' : (__NODECAST_BUILD__.source ?? 'unknown'),
        buildDirty: typeof __NODECAST_BUILD__ === 'undefined' ? null : __NODECAST_BUILD__.dirty,
        ...(playbackAttemptId ? { playbackAttemptId } : {}),
        ...browser,
        ...(safeContext ? { ctx: safeContext } : {}),
    };
    ringBuffer.push(entry);
    if (ringBuffer.length > BUFFER_LIMIT) ringBuffer.shift();
    if (pending.length >= BUFFER_LIMIT) {
        const dropIndex = Math.min(inFlightCount, pending.length - 1);
        pending.splice(dropIndex, 1);
        transportDrops.overflow++;
    }
    pending.push({ entry, retries: 0 });
    return entry;
}

function consoleEmit(entry: LogEntry): void {
    if (!import.meta.env.DEV) return;
    const tag = `[${entry.level}]`;
    const args = entry.ctx ? [tag, entry.msg, entry.ctx] : [tag, entry.msg];
    switch (entry.level) {
        case 'debug':
            console.debug(...args);
            break;
        case 'info':
            console.info(...args);
            break;
        case 'warn':
            console.warn(...args);
            break;
        case 'error':
        case 'fatal':
            console.error(...args);
            break;
    }
}

function nextFlushDelay(defaultDelay = BATCH_INTERVAL_MS): number {
    const backoffMs = remoteBackoffUntil - Date.now();
    return Math.max(defaultDelay, backoffMs > 0 ? backoffMs : 0);
}

function scheduleFlush(delayMs = BATCH_INTERVAL_MS): void {
    if (remoteAuthBlocked) return;
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
        flushTimer = null;
        void flush();
    }, nextFlushDelay(delayMs));
}

async function flush(opts: { sync?: boolean } = {}): Promise<void> {
    if (pending.length === 0) return;
    if (remoteAuthBlocked || flushInFlight) return;

    if (!opts.sync) {
        if (remoteBackoffUntil > Date.now()) {
            scheduleFlush(0);
            return;
        }
    }

    if (!remoteTransportEnabled()) return;

    const batch: typeof pending = [];
    let payload = '';
    while (batch.length < MAX_BATCH_ENTRIES && batch.length < pending.length) {
        const item = pending[batch.length]!;
        const candidate = [...batch, item];
        const nextPayload = JSON.stringify({ entries: candidate.map(({ entry }) => entry) });
        if (new Blob([nextPayload]).size > MAX_BATCH_BYTES) {
            if (batch.length === 0) {
                pending.shift();
                transportDrops.oversized++;
                continue;
            }
            break;
        }
        batch.push(item);
        payload = nextPayload;
    }
    if (batch.length === 0) return;

    if (opts.sync && typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
        try {
            if (navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }))) {
                // A true result means the browser queued it, not that the
                // server accepted it. Do not resend the same unload batch.
                removePendingPrefix(batch);
                return;
            }
        } catch {
            // fall through to fetch
        }
    }
    flushInFlight = true;
    inFlightCount = batch.length;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
        const res = await fetch(ENDPOINT, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json' },
            body: payload,
            keepalive: true,
            signal: controller.signal,
        });
        if (res.ok) {
            try {
                const ack = await withDeadline(res.json() as Promise<{ accepted?: unknown }>);
                if (Number.isInteger(ack?.accepted) && Number(ack.accepted) >= 0 && Number(ack.accepted) <= batch.length) {
                    const accepted = Number(ack.accepted);
                    if (accepted > 0) removePendingPrefix(batch.slice(0, accepted));
                    if (accepted < batch.length) retryItems(batch.slice(accepted));
                } else {
                    retryItems(batch);
                }
            } catch {
                retryItems(batch);
            }
        } else if (res.status === 401) {
            // The authenticated queue remains intact; inspect its full bounded
            // contents so a crash after a large boot/navigation prefix is not
            // stranded behind the first transport batch.
            enqueuePreAuthCrashes(pending.map(({ entry }) => entry));
            remoteAuthBlocked = true;
        } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
            removePendingPrefix(batch);
            transportDrops.rejected += batch.length;
        } else {
            if (res.status === 429) remoteBackoffUntil = Date.now() + RATE_LIMIT_BACKOFF_MS;
            retryItems(batch);
        }
    } catch {
        retryItems(batch);
    } finally {
        clearTimeout(timeout);
        flushInFlight = false;
        inFlightCount = 0;
        if (retryFlushRequested) {
            if (flushTimer) clearTimeout(flushTimer);
            flushTimer = null;
            retryFlushRequested = false;
        }
        if (remoteAuthBlocked) schedulePreAuthFlush(0);
        if (pending.length > 0 && !remoteAuthBlocked) scheduleFlush(remoteBackoffUntil > Date.now() ? 0 : BATCH_INTERVAL_MS);
    }
}

function enqueuePreAuthCrashes(entries: LogEntry[]): void {
    const crashTypes = new Set(['vue.error_handler', 'window.error', 'unhandledRejection']);
    const events = entries
        .filter((entry) => (entry.level === 'error' || entry.level === 'fatal') && crashTypes.has(entry.msg))
        .map((entry) => ({
            eventId: entry.eventId,
            type: entry.msg,
            release: entry.release,
            browser: entry.browser,
            clientTs: entry.ts,
        }));
    if (!events.length || !remoteTransportEnabled()) return;
    for (const event of events) {
        if (preAuthPending.some((item) => item.event.eventId === event.eventId)) continue;
        if (preAuthPending.length >= PREAUTH_BUFFER_LIMIT) {
            preAuthPending.shift();
            transportDrops.overflow++;
        }
        preAuthPending.push({ event, retries: 0 });
    }
    schedulePreAuthFlush();
}

function removePendingPrefix(batch: typeof pending): void {
    for (let i = 0; i < batch.length; i++) {
        if (pending[i] !== batch[i]) return;
    }
    pending.splice(0, batch.length);
}

function retryItems(batch: typeof pending): void {
    let maxRetry = 0;
    for (const item of batch) {
        if (!pending.includes(item)) continue;
        item.retries++;
        if (item.retries > MAX_RETRIES) {
            const index = pending.indexOf(item);
            if (index >= 0) pending.splice(index, 1);
            transportDrops.retriesExhausted++;
        } else {
            maxRetry = Math.max(maxRetry, item.retries);
        }
    }
    if (maxRetry > 0) {
        retryFlushRequested = true;
        remoteBackoffUntil = Math.max(remoteBackoffUntil, Date.now() + RETRY_DELAYS_MS[maxRetry - 1]!);
    }
}

function schedulePreAuthFlush(delayMs = 250): void {
    if (!remoteAuthBlocked || preAuthPending.length === 0 || preAuthFlushTimer) return;
    preAuthFlushTimer = setTimeout(() => {
        preAuthFlushTimer = null;
        void flushPreAuthCrashes();
    }, delayMs);
}

async function flushPreAuthCrashes(): Promise<void> {
    if (preAuthFlushInFlight || preAuthPending.length === 0 || !remoteTransportEnabled()) return;
    const batch = preAuthPending.slice(0, 5);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    preAuthFlushInFlight = true;
    try {
        const res = await fetch(`${ENDPOINT}/preauth`, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ events: batch.map(({ event }) => event) }),
            keepalive: true, signal: controller.signal,
        });
        if (res.ok) {
            const ack = await withDeadline(res.json() as Promise<{ accepted?: unknown }>);
            const accepted = Number.isInteger(ack?.accepted) && Number(ack.accepted) >= 0 && Number(ack.accepted) <= batch.length
                ? Number(ack.accepted)
                : 0;
            removePreAuthPrefix(batch.slice(0, accepted));
            retryPreAuth(batch.slice(accepted));
        } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
            preAuthPending.splice(0, batch.length);
            transportDrops.rejected += batch.length;
        } else {
            retryPreAuth(batch);
        }
    } catch {
        retryPreAuth(batch);
    } finally {
        clearTimeout(timeout);
        preAuthFlushInFlight = false;
        if (preAuthPending.length > 0) schedulePreAuthFlush(1_000);
    }
}

function retryPreAuth(batch: typeof preAuthPending): void {
    let maxRetry = 0;
    for (const item of batch) {
        if (!preAuthPending.includes(item)) continue;
        item.retries++;
        if (item.retries > MAX_RETRIES) {
            const index = preAuthPending.indexOf(item);
            if (index >= 0) preAuthPending.splice(index, 1);
            transportDrops.retriesExhausted++;
        } else maxRetry = Math.max(maxRetry, item.retries);
    }
    if (maxRetry > 0) {
        const delay = RETRY_DELAYS_MS[maxRetry - 1]!;
        if (preAuthFlushTimer) clearTimeout(preAuthFlushTimer);
        preAuthFlushTimer = setTimeout(() => {
            preAuthFlushTimer = null;
            void flushPreAuthCrashes();
        }, delay);
    }
}

function withDeadline<T>(promise: Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('clientlog acknowledgement timeout')), REQUEST_TIMEOUT_MS);
        promise.then(
            (value) => { clearTimeout(timer); resolve(value); },
            (error: unknown) => { clearTimeout(timer); reject(error); },
        );
    });
}

function removePreAuthPrefix(batch: typeof preAuthPending): void {
    for (let i = 0; i < batch.length; i++) {
        if (preAuthPending[i] !== batch[i]) return;
    }
    preAuthPending.splice(0, batch.length);
}

function emit(level: LogLevel, msg: string, ctx?: Record<string, unknown>): void {
    if (!shouldEmit(level)) return;
    const entry = pushEntry(level, msg, ctx);
    consoleEmit(entry);
    if (level === 'error' || level === 'fatal') {
        // Send the first error immediately so a hard refresh cannot lose the
        // trace, but coalesce error storms into the normal batch transport.
        // Player decode failures can emit dozens of hls.js errors per second;
        // flushing every one turns observability into /api/clientlog 429s.
        const now = Date.now();
        if (remoteAuthBlocked) enqueuePreAuthCrashes([entry]);
        if (now - lastImmediateErrorFlushAt >= ERROR_FLUSH_THROTTLE_MS) {
            lastImmediateErrorFlushAt = now;
            void flush();
        } else {
            scheduleFlush(ERROR_FLUSH_THROTTLE_MS);
        }
        return;
    }
    scheduleFlush();
}

if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        void flush({ sync: true });
    });
    window.addEventListener('error', (ev) => {
        // ResizeObserver loop notifications are a benign browser warning
        // — Vidstack/Lit's internal ResizeObserver fires its callback,
        // which triggers a layout change, which queues another callback,
        // and the browser surfaces this as an `error` event. There's no
        // application-level fault; suppressing it stops a flood of
        // clientlog POSTs that hit the server's rate-limit (429) and
        // drown out real errors. See:
        //   https://github.com/WICG/resize-observer/issues/38
        if (
            ev.message
            && /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/.test(ev.message)
        ) {
            return;
        }
        emit('error', 'window.error', {
            message: ev.message,
            source: ev.filename,
            line: ev.lineno,
            col: ev.colno,
            stack: ev.error?.stack,
        });
    });
    window.addEventListener('unhandledrejection', (ev) => {
        const reason = ev.reason;
        emit('error', 'unhandledRejection', {
            message: reason?.message ?? String(reason),
            stack: reason?.stack,
        });
    });
}

export const log = {
    debug: (msg: string, ctx?: Record<string, unknown>) => emit('debug', msg, ctx),
    info: (msg: string, ctx?: Record<string, unknown>) => emit('info', msg, ctx),
    warn: (msg: string, ctx?: Record<string, unknown>) => emit('warn', msg, ctx),
    error: (msg: string, ctx?: Record<string, unknown>) => emit('error', msg, ctx),
    fatal: (msg: string, ctx?: Record<string, unknown>) => emit('fatal', msg, ctx),
    /** Inspect the in-memory ring buffer (read-only copy). */
    dump: (): readonly LogEntry[] => ringBuffer.slice(),
    /** Local transport state; reading this never creates another log event. */
    transportState: () => ({ pending: pending.length, preAuthPending: preAuthPending.length, authBlocked: remoteAuthBlocked, drops: { ...transportDrops } }),
    /** Resume the retained bounded queue after a successful login/setup. */
    resumeTransport: () => {
        remoteAuthBlocked = false;
        remoteBackoffUntil = 0;
        if (pending.length > 0) {
            if (flushTimer) clearTimeout(flushTimer);
            flushTimer = null;
            scheduleFlush(0);
        }
    },
    /** Force-flush right now. */
    flush: () => flush(),
    /** Adjust the level filter at runtime (also persists to localStorage). */
    setLevel: (lvl: LogLevel) => {
        threshold = lvl;
        try {
            localStorage.setItem('log.level', lvl);
        } catch {
            // ignore
        }
    },
    /** Stable correlation ID for this session. */
    sessionId: SESSION_ID,
    /** Active live-playback attempt; copied into every structured client report. */
    setPlaybackAttemptId: (id: string | null) => {
        activePlaybackAttemptId = validPlaybackAttemptId(id) ? id : null;
    },
};

if (typeof window !== 'undefined') {
    // Expose for ad-hoc console debugging and the future /dev/logs page.
    (window as unknown as { log: typeof log }).log = log;
}
